import {
  Bot,
  Check,
  CircleDot,
  Flag,
  GitMerge,
  LoaderCircle,
  Network,
  Pause,
  Play,
  ShieldCheck,
  TriangleAlert,
} from "lucide-react";
import { Handle, Position, type Node, type NodeProps } from "@xyflow/react";
import type { WorkflowNodeData } from "../../shared/types";

export type AgentFlowNode = Node<WorkflowNodeData, "agentNode">;

const kindIcons = {
  trigger: Play,
  orchestrator: Network,
  agent: Bot,
  merge: GitMerge,
  approval: ShieldCheck,
  output: Flag,
};

function StatusIcon({ status }: { status: WorkflowNodeData["status"] }) {
  if (status === "running") return <LoaderCircle className="spin" size={13} />;
  if (status === "completed") return <Check size={13} />;
  if (status === "error") return <TriangleAlert size={13} />;
  if (status === "waiting") return <Pause size={13} />;
  return <CircleDot size={11} />;
}

const policyLabels = {
  economical: "Econômico",
  balanced: "Balanceado",
  deep: "Profundo",
};

export function AgentNode({ data, selected }: NodeProps<AgentFlowNode>) {
  const Icon = kindIcons[data.kind];
  const status = data.status ?? "idle";
  const hasTarget = data.kind !== "trigger";
  const hasSource = data.kind !== "output";

  return (
    <div
      className={`flow-node flow-node--${data.kind} flow-node--${status} ${selected ? "is-selected" : ""}`}
    >
      {hasTarget && <Handle type="target" position={Position.Left} className="node-handle" />}
      <div className="flow-node__stripe" />
      <div className="flow-node__icon"><Icon size={16} /></div>
      <div className="flow-node__content">
        <div className="flow-node__title-row">
          <strong>{data.label}</strong>
          <span className={`node-status node-status--${status}`} title={status}>
            <StatusIcon status={status} />
          </span>
        </div>
        <span className="flow-node__description">{data.description}</span>
        {data.modelPolicy && (
          <span className="flow-node__meta">
            {policyLabels[data.modelPolicy]}
            {data.provider === "demo" ? " · Demo" : data.model ? ` · ${data.model}` : ""}
            {data.onError === "continue" ? " · resiliente" : ""}
          </span>
        )}
      </div>
      {hasSource && <Handle type="source" position={Position.Right} className="node-handle" />}
    </div>
  );
}
